import { ArrowRight, Check, LoaderCircle } from "lucide-react";
import { FormEvent, useCallback, useId, useMemo, useState } from "react";
import { TurnstileWidget } from "./TurnstileWidget";

type WaitlistFormProps = {
  variant?: "hero" | "full";
};

type FormState = "email" | "source" | "loading" | "success" | "error";

const sourceOptions = [
  "A friend or classmate",
  "LinkedIn",
  "X / Twitter",
  "College community",
  "A job-search community",
  "Search",
  "Something else"
] as const;

const isValidEmail = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

export function WaitlistForm({ variant = "hero" }: WaitlistFormProps) {
  const emailId = useId();
  const sourceId = useId();
  const [email, setEmail] = useState("");
  const [source, setSource] = useState("");
  const [website, setWebsite] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileAttempt, setTurnstileAttempt] = useState(0);
  const [formState, setFormState] = useState<FormState>("email");
  const [message, setMessage] = useState("");

  const demoMode = useMemo(
    () => import.meta.env.VITE_WAITLIST_MODE === "demo" || import.meta.env.DEV,
    []
  );
  const turnstileSiteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const handleTurnstileError = useCallback(() => {
    setTurnstileToken("");
    setMessage("The security check needs another try.");
  }, []);
  const handleTurnstileToken = useCallback((token: string) => {
    setTurnstileToken(token);
    if (token) setMessage("");
  }, []);

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (formState === "email" || formState === "error") {
      if (!isValidEmail(email)) {
        setFormState("error");
        setMessage("Enter a valid email so we know where to send your invite.");
        return;
      }

      setMessage("");
      setFormState("source");
      return;
    }

    if (formState !== "source") return;

    if (!source) {
      setMessage("Choose where you first heard about Offr.");
      return;
    }

    if (!demoMode && (!turnstileSiteKey || !turnstileToken)) {
      setMessage(
        turnstileSiteKey
          ? "Complete the security check and try again."
          : "The waitlist security check is not configured yet."
      );
      return;
    }

    setFormState("loading");
    setMessage("");

    try {
      if (demoMode) {
        const existing = JSON.parse(
          window.localStorage.getItem("offr-waitlist-demo") ?? "[]"
        ) as Array<{ email: string; source: string; joinedAt: string }>;

        const next = [
          ...existing.filter((entry) => entry.email !== email.trim().toLowerCase()),
          {
            email: email.trim().toLowerCase(),
            source,
            joinedAt: new Date().toISOString()
          }
        ];
        window.localStorage.setItem("offr-waitlist-demo", JSON.stringify(next));
        await new Promise((resolve) => window.setTimeout(resolve, 550));
      } else {
        const response = await fetch("/api/waitlist", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim().toLowerCase(),
            source,
            website,
            turnstileToken
          })
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as
            | { message?: string }
            | null;
          throw new Error(
            payload?.message ?? "We could not save your place just yet."
          );
        }
      }

      setFormState("success");
      setMessage(
        demoMode
          ? "Saved locally in demo mode. Connect Supabase before launch."
          : "You’re in. We’ll send launch updates, not application spam."
      );
      window.dispatchEvent(
        new CustomEvent("offr:waitlist-submitted", {
          detail: { placement: variant }
        })
      );
    } catch (error) {
      setTurnstileToken("");
      setTurnstileAttempt((current) => current + 1);
      setFormState("error");
      setMessage(
        error instanceof Error
          ? error.message
          : "We could not save your place. Please try again."
      );
    }
  };

  if (formState === "success") {
    return (
      <div className={`waitlist-success waitlist-success--${variant}`} role="status">
        <span className="waitlist-success__icon">
          <Check aria-hidden="true" size={18} strokeWidth={2.4} />
        </span>
        <span>
          <strong>Spot saved.</strong>
          <small>{message}</small>
        </span>
      </div>
    );
  }

  const askingSource = formState === "source" || formState === "loading";

  return (
    <form
      className={`waitlist-form waitlist-form--${variant} ${
        askingSource ? "waitlist-form--source" : ""
      } ${!demoMode && askingSource ? "waitlist-form--protected" : ""}`}
      onSubmit={submit}
      noValidate
    >
      <div className="hp-field" aria-hidden="true">
        <label htmlFor={`${emailId}-website`}>Website</label>
        <input
          id={`${emailId}-website`}
          name="website"
          value={website}
          onChange={(event) => setWebsite(event.target.value)}
          tabIndex={-1}
          autoComplete="off"
        />
      </div>

      {!askingSource ? (
        <>
          <label className="sr-only" htmlFor={emailId}>
            Email address
          </label>
          <input
            id={emailId}
            type="email"
            name="email"
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              if (formState === "error") {
                setFormState("email");
                setMessage("");
              }
            }}
            placeholder={variant === "hero" ? "you@email.com" : "Enter your email"}
            autoComplete="email"
            aria-describedby={message ? `${emailId}-message` : undefined}
          />
          <button type="submit">
            {variant === "hero" ? "Notify me at launch" : "Join waitlist"}
            <ArrowRight aria-hidden="true" size={18} strokeWidth={2} />
          </button>
        </>
      ) : (
        <>
          <span className="waitlist-form__email" title={email}>
            {email}
          </span>
          <label className="sr-only" htmlFor={sourceId}>
            Where did you hear about Offr?
          </label>
          <select
            id={sourceId}
            name="source"
            value={source}
            onChange={(event) => {
              setSource(event.target.value);
              setMessage("");
            }}
            disabled={formState === "loading"}
            required
          >
            <option value="">How did you find Offr?</option>
            {sourceOptions.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <button type="submit" disabled={formState === "loading"}>
            {formState === "loading" ? (
              <>
                Saving
                <LoaderCircle
                  className="spin"
                  aria-hidden="true"
                  size={18}
                  strokeWidth={2}
                />
              </>
            ) : (
              <>
                Save my spot
                <ArrowRight aria-hidden="true" size={18} strokeWidth={2} />
              </>
            )}
          </button>
          {!demoMode && turnstileSiteKey ? (
            <TurnstileWidget
              key={turnstileAttempt}
              siteKey={turnstileSiteKey}
              onToken={handleTurnstileToken}
              onError={handleTurnstileError}
            />
          ) : null}
        </>
      )}

      <p
        className={`waitlist-form__message ${
          formState === "error" ? "is-error" : ""
        }`}
        id={`${emailId}-message`}
        aria-live="polite"
      >
        {message ||
          (askingSource
            ? !demoMode && turnstileSiteKey && !turnstileToken
              ? "Checking this submission before saving your place."
              : "One quick question, then your place is saved."
            : variant === "full"
              ? demoMode
                ? "Local demo mode — saved only in this browser."
                : "Early access updates only. Unsubscribe anytime."
              : "")}
      </p>
      {variant === "hero" && !askingSource ? (
        <p className="waitlist-form__early-note">
          <span className="waitlist-form__early-dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span>Joining the first group — huge discounts at launch.</span>
        </p>
      ) : null}
    </form>
  );
}
